import { disc, hash, px } from './art';

/**
 * Dad's lab (the hub), painted in code: panelled walls, a window onto Earth's night sky, hanging
 * cables, a metal floor and the glowing pad the suit stands on.
 */

type Ctx = CanvasRenderingContext2D;

export const LAB = {
  wall: '#161c30',
  wallLit: '#1d2540',
  seam: '#0d1120',
  rivet: '#2e3858',
  frame: '#2e3858',
  frameLit: '#46557e',
  sky: '#0c1030',
  skyLow: '#1a2050',
  star: '#fff6d8',
  moon: '#e8d9b0',
  cable: '#0a0d18',
  floor: '#232b40',
  floorLit: '#3a4766',
  floorDark: '#151a28',
  pad: '#7df9ff',
  padDim: '#2f7f86',
} as const;

/** Where the suit stands (feet), and the floor line. */
export const LAB_PAD = { x: 70, floorFromBottom: 40 } as const;

export function paintLab(ctx: Ctx, width: number, height: number): void {
  // Walls: two tones in bands, panel seams with rivets.
  for (let y = 0; y < height; y++) px(ctx, y % 32 < 16 ? LAB.wall : LAB.wallLit, 0, y, width, 1);
  for (let x = 0; x < width; x += 48) {
    px(ctx, LAB.seam, x, 0, 2, height);
    for (let y = 8; y < height; y += 32) px(ctx, LAB.rivet, x + 4, y, 2, 2);
  }

  // The window onto Earth's night sky.
  const wx = 20;
  const wy = 14;
  const ww = 150;
  const wh = 70;
  px(ctx, LAB.frame, wx - 3, wy - 3, ww + 6, wh + 6);
  px(ctx, LAB.frameLit, wx - 3, wy - 3, ww + 6, 1);
  for (let y = 0; y < wh; y++) px(ctx, y < wh * 0.6 ? LAB.sky : LAB.skyLow, wx, wy + y, ww, 1);
  for (let i = 0; i < 26; i++) {
    px(
      ctx,
      LAB.star,
      wx + Math.floor(hash(i, 1, 21) * ww),
      wy + Math.floor(hash(i, 2, 21) * wh * 0.7),
    );
  }
  disc(ctx, wx + ww - 30, wy + 20, 9, LAB.moon);
  // Window bars.
  px(ctx, LAB.frame, wx + ww / 3, wy, 2, wh);
  px(ctx, LAB.frame, wx + (ww * 2) / 3, wy, 2, wh);

  // Cables hanging from the ceiling.
  for (const [x0, x1, sag] of [
    [190, 300, 26],
    [250, 420, 40],
    [360, 560, 22],
  ] as const) {
    for (let x = x0; x <= x1 && x < width; x++) {
      const t = (x - x0) / (x1 - x0);
      px(ctx, LAB.cable, x, Math.round(4 + Math.sin(t * Math.PI) * sag), 1, 2);
    }
  }

  // Metal floor with a lit edge and plate seams.
  const fy = height - LAB_PAD.floorFromBottom;
  px(ctx, LAB.floor, 0, fy, width, LAB_PAD.floorFromBottom);
  px(ctx, LAB.floorLit, 0, fy, width, 2);
  for (let x = 0; x < width; x += 32) px(ctx, LAB.floorDark, x, fy + 2, 1, LAB_PAD.floorFromBottom);

  // The suit's pad: a glowing disc set in the floor.
  for (let x = -26; x <= 26; x++) {
    const h = Math.round(Math.sqrt(1 - (x / 27) ** 2) * 5);
    px(ctx, Math.abs(x) > 20 ? LAB.padDim : LAB.pad, LAB_PAD.x + x, fy - 1, 1, 1);
    px(ctx, LAB.padDim, LAB_PAD.x + x, fy + 1, 1, h);
  }
}
